'use client';

import React, { useContext, useEffect, useState } from 'react';
import AmplitudeScript from '~/components/ThirdPartyScripts/AmplitudeScript';
import AttentiveScript from '~/components/ThirdPartyScripts/AttentiveScript';
import GoogleAnalyticsScripts from '~/components/ThirdPartyScripts/GoogleAnalyticsScripts';
import ImpactTracker from '~/components/ThirdPartyScripts/ImpactTracker';
import MntnTrackingPixel from '~/components/ThirdPartyScripts/MntnTrackingPixel';
import RedditScript from '~/components/ThirdPartyScripts/RedditScript';
import TikTokScript from '~/components/ThirdPartyScripts/TikTokScript';
import { setKnownCookie } from '~/hooks/cookies';
import {
  CCPA_OPT_IN_VALUE,
  CCPA_OPT_OUT_VALUE,
  createPrivacyConsentModel,
  PrivacyConsentModel,
  shouldLoadThirdPartyScripts,
} from '~/lib/privacy/model';

type IPrivacyConsentContext = {
  state: PrivacyConsentModel;
  setState: React.Dispatch<React.SetStateAction<PrivacyConsentModel>>;
};

const PrivacyConsentContext = React.createContext<IPrivacyConsentContext>({
  state: {
    state: 'unknown',
  },
  setState: () => {},
});

export function PrivacyConsentProvider({
  children,
  initialModel,
}: {
  children: React.ReactNode;
  initialModel: PrivacyConsentModel;
}) {
  const [model, setModel] = useState(initialModel);
  useEffect(() => {
    if (model.state === 'unknown' && 'globalPrivacyControl' in navigator) {
      const optOut = !!navigator.globalPrivacyControl;

      const cookieValue = optOut ? CCPA_OPT_OUT_VALUE : CCPA_OPT_IN_VALUE;
      setKnownCookie(undefined, 'ccpa', cookieValue);
      setModel(createPrivacyConsentModel(cookieValue));
    }
  }, [model.state]);

  return (
    <PrivacyConsentContext
      value={{
        state: model,
        setState: setModel,
      }}
    >
      {children}

      {shouldLoadThirdPartyScripts(model) && (
        <>
          <ImpactTracker />
          <GoogleAnalyticsScripts />
          <AmplitudeScript />
          <RedditScript />
          <TikTokScript />
          <AttentiveScript />
          <MntnTrackingPixel />
        </>
      )}
    </PrivacyConsentContext>
  );
}

export function usePrivacyConsent() {
  const context = useContext(PrivacyConsentContext);
  if (!context) {
    throw new Error(
      'usePrivacyConsent must be used within a PrivacyConsentProvider',
    );
  }
  return context;
}
